import React, { useEffect, useState, useRef, useCallback } from 'react';
import { InputAdornment, CircularProgress, SvgIcon, Link, Box, Button, Popover, Grid, IconButton, Checkbox, Stack, FormGroup, Typography, FormControl, RadioGroup, FormControlLabel, Radio, Divider, TextField, Slide, Drawer, useMediaQuery } from '@mui/material'
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { debounce } from 'lodash';
import { cleanData, clusteredData } from 'data/city.js';
import { useTheme } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useBackNavigationDialog } from 'hooks/useBackNavigationDialog';

function TabPanel(props) {

    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

/**
 * Generates accessibility properties for a tab component.
 *
 * @param {number} index - The index of the tab.
 * @returns {object} An object containing the id and aria-controls attributes 
 * for the tab and its associated tab panel.
 */
function a11yProps(index) {
    return {
        id: `vertical-tab-${index}`,
        'aria-controls': `vertical-tabpanel-${index}`,
    };
}
/**
 * FilterSearch is a component that provides a user interface for filtering and searching job applications.
 * 
 * @param {Array} preferedLocation - List of preferred job locations for filtering.
 * @param {string} jobTypes - Types of jobs to be displayed (e.g., "both", "Full time").
 * @param {Function} fetchJobPreferences - Function to fetch job preferences.
 * @param {Function} fetchJobs - Function to fetch jobs based on criteria.
 * @param {string} activeTab - The active tab in the job application view.
 * @param {boolean} loading - Indicates if the job data is currently being loaded.
 * @param {Function} setFilterLoading - Function to set the loading state for filters.
 * @param {boolean} filterApplied - Indicates if a filter has been applied.
 * @param {Function} setOpenSearch - Function to control the opening of the search input.
 * @param {Function} setFilterApplied - Function to set the state indicating if a filter is applied.
 * @param {number} length - The number of job results currently displayed.
 * @param {string} categoryFilter - The category filter applied to the job search.
 */

const FilterSearch = ({ preferedLocation, jobTypes, fetchJobPreferences, fetchJobs, activeTab, loading, setFilterLoading, filterApplied, setOpenSearch, setFilterApplied, length, categoryFilter }) => {

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    useEffect(() => {
        fetchJobPreferences();
    }, []);

    useEffect(() => {
        if (!filterApplied) {
            setSortCriteria({});
            setFilterCriteria({
                location: [],
                work_format: [],
                job_type: []
            })
        }
    }, [filterApplied])

    // job track menu
    const [anchorEl, setAnchorEl] = React.useState(null);
    const { openDialog, closeDialog, dialogLocation } = useBackNavigationDialog();
    const dialogIds = dialogLocation.state?.dialogIds || [];
    const isFilterOpen = dialogIds.includes('filterPopover');

    // const open = Boolean(anchorEl);

    // filter by tabs
    const [value, setValue] = React.useState(0);

    // show hide filter search
    const containerRef = React.useRef(null);
    // const [showFilter, setShowFilter] = React.useState(true);
    const [showSearch, setShowSearch] = React.useState(false);
    const [searchValue, setSearchValue] = useState('');
    const inputRef = useRef(null);
    const [category, setCategory] = useState(categoryFilter ? categoryFilter : "");

    const [filterCriteria, setFilterCriteria] = useState({
        location: [],
        work_format: [],
        job_type: []
    });

    const [sortCriteria, setSortCriteria] = useState({
        salary: "",
        updatedAt: "",
    });
    const [sortValue, setSortValue] = useState(null);

    /**
     * Handles the open event for a popover.
     *
     * This function is called when a popover should be opened. It sets the
     * anchor element to the element that triggered the event, and sets the
     * popover open state to true.
     *
     * @param {React.MouseEvent<HTMLDivElement>} event - The event that triggered
     *   the popover open.
     */
    const handlePopoverOpen = (event) => {
        setAnchorEl(event.currentTarget);
        openDialog('filterPopover');
    };

    /**
     * Handles the close event for a popover.
     *
     * This function is called when a popover should be closed. It resets the
     * anchor element and the popover open state to null. If the `isCancel`
     * parameter is true, it will also reset the filter criteria and sort
     * criteria to their default values, and fetch the jobs with the default
     * filters and sort.
     *
     * @param {boolean} isCancel - Whether the popover was closed by cancelling
     *   the filter/search.
     */
    const handlePopoverClose = (isCancel) => {
        setAnchorEl(null);
        closeDialog('filterPopover');
        const isFilterApplied = (filterCriteria?.location?.length || filterCriteria?.work_format?.length || filterCriteria?.job_type?.length);
        const isSortingApplied = Object?.keys(sortCriteria)?.length;
        if (isCancel && (isFilterApplied || isSortingApplied || filterApplied)) {
            setFilterApplied(false);
            setSortCriteria({});
            setFilterCriteria({
                location: [],
                work_format: [],
                job_type: []
            })
            fetchJobs(null, false, searchValue, {
                location: [],
                work_format: [],
                job_type: []
            }, {}, activeTab, null, true);
            setFilterLoading(true);
        }
    };

    /**
     * Applies the current filter and sort criteria for job listings.
     *
     * This function checks if there are any active filters or sorting criteria.
     * If so, it sets the loading state, applies the filter, and fetches the
     * jobs with the current filter and sort criteria. Closes the popover
     * afterwards.
     */

    const handleApplyFilter = async () => {
        if (!sortCriteria && filterCriteria?.location?.length === 0 && filterCriteria?.work_format?.length === 0 && filterCriteria?.job_type?.length === 0) {
            return;
        }
        setFilterLoading(true);
        setFilterApplied(true);
        fetchJobs(null, false, searchValue, filterCriteria, sortCriteria, activeTab, category, true);
        handlePopoverClose();
    };

    /**
     * Updates the state with a new value when the event is triggered.
     *
     * @param {object} event - The event that triggered the change.
     * @param {string} newValue - The new value to be set in the state.
     */

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    /**
     * Toggles the visibility of the search bar to show the search input field
     * and hide the filter criteria. If the search input field is shown, the
     * function will focus on the input field after a short delay.
     */
    const handleShowSearch = () => {
        setShowSearch(true);
        // setShowFilter(false);
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 300);
    };

    /**
     * Clears the search input and reloads the job listings with the current 
     * filter and sort criteria if there is a search value present.
     */

    const handleCloseSearchJob = () => {
        if (searchValue) {
            setSearchValue('');
            setFilterLoading(true);
            fetchJobs(null, false, null, filterCriteria, sortCriteria, activeTab, null, true);
        }
    };
    /**
     * Toggles the visibility of the search bar and the filter criteria.
     * The search bar is hidden and the filter criteria is shown.
     * Additionally, the function will close the search bar by clearing
     * the search value and reloading the job listings using the current
     * filter criteria, sort criteria, and active tab.
     */
    const handleShowHide = () => {
        setShowSearch(false);
        // setShowFilter(true);
        handleCloseSearchJob();
    }
    /**
     * Updates the sort criteria based on the changed input event.
     *
     * This function extracts the name and value from the event target
     * and updates the existing sort criteria state with the new value
     * for the specified name.
     *
     * @param {object} event - The event that triggered the change, containing
     * the name and value of the sort criteria to be updated.
     */
    const handleSortChange = (event) => {
        const { name, value } = event.target;

        console.log(value);
        setSortValue(value);

        if (value === '1' || value === '0') {
            setSortCriteria({
                salary: value,
            });
        } else {
            setSortCriteria({
                updatedAt: value === '11' ? '1' : '0'
            })
        }
    };

    /**
     * Handles the double click event on the sort criteria chips.
     * If the same criteria is double clicked, it will be reset to an empty string.
     * @param {object} event - The event that triggered the double click.
     */
    const handleDoubleClick = (event) => {
        const { name, value } = event.target;
        console.log(value);
        if (value === '1' || value === '0') {
            setSortCriteria({
                salary: value,
            });
        } else {
            setSortCriteria({
                updatedAt: value === '11' ? '1' : '0'
            })
        }
    };

    const debouncedSearch = useCallback(
        debounce(async (value) => {
            setFilterLoading(true);
            setOpenSearch(value ? true : false);
            fetchJobs(null, false, value, filterCriteria, sortCriteria, activeTab, category, true);
        }, 500),
        []
    );

    /**
     * Handles the search input change event by updating the search value
     * state with the new input value and calling the debounced search
     * function with the new value.
     * @param {string} value - The new search input value
     */
    const handleSearchInputChange = (value) => {
        setSearchValue(value);
        // debouncedSearch(value);
    };

    /**
     * Handles the change of the filter criteria. Updates the filterCriteria
     * state with the new value and name of the changed filter criteria.
     * @param {object} event - The event that triggered the change.
     * @param {string} value - The new value of the changed filter criteria.
     */
    const handleChangeFilterCriteria = (event, value) => {
        const { name } = event.target;
        setFilterCriteria((prevCriteria) => ({
            ...prevCriteria,
            [name]: prevCriteria[name].includes(value)
                ? prevCriteria[name].filter((item) => item !== value)
                : [...prevCriteria[name], value],
        }));
    };

    /**
     * Handles the change of the job categories filter criteria. Updates the category
     * state with the new value of the changed filter criteria.
     * @param {object} e - The event that triggered the change.
     */
    const handleCategories = (e) => {
        setCategory(e.target.value);
    };
    /**
     * Handles the change of the job type filter criteria. Updates the filterCriteria
     * state with the new value of the changed filter criteria and fetches the jobs
     * with the updated filter criteria.
     * @param {string} value - The new value of the changed filter criteria.
     */
    const handleFetchJobFromJobType = async (value) => {
        setFilterLoading(true);
        const jobTypeFilter =
            value === 'both'
                ? ['Full time', 'Part time', 'Temporary', 'Internship']
                : value === 'Full time'
                    ? ['Full time']
                    : ['Part time', 'Temporary', 'Internship'];

        const updatedFilter = {
            ...filterCriteria,
            job_type: jobTypeFilter,
        };

        setFilterCriteria(updatedFilter);

        await fetchJobs(null, false, null, updatedFilter, sortCriteria, activeTab, category, true);
    };

    const handleSearch = async (value) => {
        setFilterLoading(true);
        setOpenSearch(value ? true : false);
        await fetchJobs(null, false, value, filterCriteria, sortCriteria, activeTab, category, true);
    };

    return (
        <>
            <Box className="header-wrapper" ref={containerRef}>
                <Box className="filter-wrapper" mb={2}>
                    <Stack direction="row" justifyContent={'space-between'}>
                        <Typography variant="p" sx={{ fontSize: '14px', fontWeight: '500', lineHeight: '20px', textAlign: 'left' }}>
                            {length <= 0 ? 'Result Not Found' : activeTab === 'saved-jobs'
                                ? 'Jobs you saved for later application'
                                : activeTab === 'applied-jobs'
                                    ? 'Jobs you either applied to or initiated application'
                                    : 'No Applied Jobs Results found'}
                        </Typography>
                        <Box>
                            <Stack direction="row" spacing={'10px'}>
                                <IconButton
                                    aria-label="update"
                                    aria-owns={isFilterOpen || filterApplied ? 'filter-by' : undefined}
                                    aria-haspopup="true"
                                    onClick={handlePopoverOpen}
                                >
                                    {filterApplied && <SvgIcon className="ellipse-icon">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" fill="none">
                                            <circle cx="6" cy="6.5" r="6" fill="#C12929" />
                                        </svg>
                                    </SvgIcon>}
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="16" viewBox="0 0 18 16" fill="none">
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M7.0923 12.3074H16.3846C16.7243 12.3074 17 12.0317 17 11.692C17 11.3523 16.7243 11.0766 16.3846 11.0766H7.0923C6.80677 9.67293 5.56431 8.61508 4.07692 8.61508C2.37846 8.61508 1 9.99355 1 11.692C1 13.3905 2.37846 14.7689 4.07692 14.7689C5.56431 14.7689 6.80677 13.7111 7.0923 12.3074ZM4.07692 9.84585C5.096 9.84585 5.92308 10.6729 5.92308 11.692C5.92308 12.7111 5.096 13.5382 4.07692 13.5382C3.05785 13.5382 2.23077 12.7111 2.23077 11.692C2.23077 10.6729 3.05785 9.84585 4.07692 9.84585ZM10.9077 3.69201H1.61538C1.27569 3.69201 1 3.9677 1 4.30739C1 4.64708 1.27569 4.92278 1.61538 4.92278H10.9077C11.1932 6.32647 12.4357 7.38432 13.9231 7.38432C15.6215 7.38432 17 6.00585 17 4.30739C17 2.60893 15.6215 1.23047 13.9231 1.23047C12.4357 1.23047 11.1932 2.28832 10.9077 3.69201ZM13.9231 2.46124C14.9422 2.46124 15.7692 3.28832 15.7692 4.30739C15.7692 5.32647 14.9422 6.15355 13.9231 6.15355C12.904 6.15355 12.0769 5.32647 12.0769 4.30739C12.0769 3.28832 12.904 2.46124 13.9231 2.46124Z" fill="#9CA3AF" stroke="white" stroke-width="0.5" />
                                    </svg>
                                </IconButton>
                                <IconButton className={showSearch ? "search-active" : ""} aria-label="update" onClick={handleShowSearch}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M1.23254 7.20043C1.23254 3.9044 3.9045 1.23242 7.20052 1.23242C10.4965 1.23242 13.1685 3.9044 13.1685 7.20043C13.1685 10.4964 10.4965 13.1684 7.20052 13.1684C3.90449 13.1684 1.23254 10.4964 1.23254 7.20043ZM7.20052 0.232422C3.35221 0.232422 0.232544 3.35212 0.232544 7.20043C0.232544 11.0487 3.35221 14.1684 7.20052 14.1684C8.94474 14.1684 10.5393 13.5275 11.7615 12.4684L14.9139 15.6208C15.1092 15.8161 15.4258 15.8161 15.621 15.6208C15.8163 15.4255 15.8163 15.1089 15.621 14.9137L12.4686 11.7613C13.5277 10.5391 14.1685 8.94459 14.1685 7.20043C14.1685 3.35212 11.0488 0.232422 7.20052 0.232422Z" fill="#9CA3AF" />
                                    </svg>
                                </IconButton>
                            </Stack>
                        </Box>
                    </Stack>
                </Box>
                {showSearch &&
                    <Box className="search-wrapper">
                        <Stack direction="row" alignItems={'center'} spacing={2} sx={{ width: '100%' }}>
                            <TextField id="outlined-basic" size="small" variant="outlined" inputRef={inputRef} value={searchValue} placeholder="Enter designation / companies / location" sx={{ background: '#ffffff', borderRadius: '12px', width: '100%' }}
                                onChange={(e) => handleSearchInputChange(e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {loading ? <CircularProgress size={20} /> :
                                                <IconButton sx={{ border: 'none !important' }} edge="end" size="small" onClick={() => handleSearch(searchValue)}>
                                                    <ArrowForwardIcon />
                                                </IconButton>
                                            }
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Link onClick={handleShowHide} sx={{ 'cursor': 'pointer' }} className='cancel-btn' underline="none">Cancel</Link>
                        </Stack>
                    </Box>
                }
            </Box>
            {isMobile ? (
                <Drawer
                    anchor="bottom"
                    open={isFilterOpen}
                    onClose={() => handlePopoverClose(false)}
                    className="mobile-bottomsheet-dialog filter-by-overlay"
                >
                    <Box sx={{ height: 400, width: { xs: '100%', md: '600px' } }}>
                        <Grid container sx={{ height: '100%' }}>
                            <Grid item xs={4} md={5} className="filter-by-tabs-item">
                                <Tabs
                                    orientation="vertical"
                                    variant="scrollable"
                                    value={value}
                                    onChange={handleChange}
                                    aria-label="Vertical tabs example"
                                    sx={{ height: '100%' }}
                                >
                                    <Tab label="Sort By" disableRipple {...a11yProps(0)} />
                                    <Tab label="Location" disableRipple {...a11yProps(1)} />
                                    <Tab label="Job Suitability" disableRipple {...a11yProps(2)} />
                                    <Tab label="Job Type" disableRipple {...a11yProps(3)} />
                                    {activeTab === 'saved-jobs' && <Tab label="Application Status" disableRipple {...a11yProps(4)} />}
                                </Tabs>
                            </Grid>

                            <Grid item xs={8} md={7} className="filter-by-tabs-content">
                                <TabPanel value={value} index={0} className="tab-panel-wrapper">
                                    <Box>
                                        <Box>
                                            <Typography className="title">Sort By</Typography>
                                        </Box>
                                        <Box>
                                            <FormControl>
                                                <RadioGroup
                                                    aria-labelledby="demo-controlled-radio-1"
                                                    name="salary"
                                                    value={sortValue}
                                                    onChange={handleSortChange}
                                                >
                                                    <FormControlLabel value="1" control={<Radio onDoubleClick={handleDoubleClick} />} label="Compensation (High to Low)" />
                                                    <FormControlLabel value="0" control={<Radio onDoubleClick={handleDoubleClick} />} label="Compensation (Low to High)" />
                                                    <FormControlLabel value="11" control={<Radio onDoubleClick={handleDoubleClick} />} label="Posted (Latest First)" />
                                                    <FormControlLabel value="00" control={<Radio onDoubleClick={handleDoubleClick} />} label="Posted (Oldest First)" />
                                                </RadioGroup>
                                            </FormControl>
                                            <Divider sx={{ borderStyle: 'dashed', borderWidth: '1px', marginTop: '0.5rem', marginBottom: '0.5rem' }} />
                                        </Box>
                                    </Box>
                                </TabPanel>
                                <TabPanel value={value} index={1} className="tab-panel-wrapper">
                                    <Box>
                                        <Box>
                                            <Typography className="title">Location</Typography>
                                            <FormControl>
                                                <FormGroup>
                                                    <Box sx={{ maxHeight: '14rem', overflow: "auto", marginBottom: '16px' }}>
                                                        {preferedLocation?.map((data, index) => (
                                                            <FormControlLabel
                                                                key={index}
                                                                control={
                                                                    <Checkbox
                                                                        checked={filterCriteria?.location?.some((fl) => (clusteredData?.find(ct => ct?.value === fl)?.name === data || fl === data))}
                                                                        onChange={(e) => handleChangeFilterCriteria(e, clusteredData?.find(ct => ct?.name === data)?.value || data)}
                                                                        name="location"
                                                                    />
                                                                }
                                                                label={data}
                                                            />
                                                        ))}
                                                    </Box>
                                                </FormGroup>
                                            </FormControl>
                                        </Box>
                                    </Box>
                                </TabPanel>
                                <TabPanel value={value} index={2} className="tab-panel-wrapper">
                                    <Box>
                                        <Typography className="title">Job Suitability</Typography>
                                        <FormControl>
                                            <FormGroup>
                                                {["Highly Suitable", "Moderately Suitable", "Not Suitable"]?.map(data => (
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                name="work_format"
                                                                checked={filterCriteria?.work_format?.includes(data)}
                                                                onChange={(e) => handleChangeFilterCriteria(e, data)}
                                                                value={data} />
                                                        }
                                                        label={data} />
                                                ))}
                                            </FormGroup>
                                        </FormControl>
                                    </Box>
                                </TabPanel>
                                <TabPanel value={value} index={3} className="tab-panel-wrapper">
                                    <Box>
                                        <FormControl>
                                            <FormGroup>
                                                <Typography className="title">Job Type</Typography>
                                                <Box sx={{ maxHeight: '14rem', overflow: "auto", marginBottom: '16px' }}>
                                                    <Stack>
                                                        {["Full time", "Part time", "Temporary", "Internship"]?.map((jobType, index) => (
                                                            <FormControlLabel
                                                                key={index}
                                                                control={
                                                                    <Checkbox
                                                                        checked={filterCriteria.job_type.includes(jobType)}
                                                                        onChange={(e) => handleChangeFilterCriteria(e, jobType)}
                                                                        name="job_type"
                                                                        sx={{ padding: '8px' }}
                                                                    />
                                                                }
                                                                label={jobType}
                                                            />
                                                        ))}
                                                    </Stack>
                                                </Box>
                                            </FormGroup>
                                        </FormControl>
                                    </Box>
                                </TabPanel>
                                {activeTab === 'saved-jobs' &&
                                    <TabPanel value={value} index={4} className="tab-panel-wrapper">
                                        <Box>
                                            <Box>
                                                <Typography className="title">Application Status</Typography>
                                            </Box>
                                            <Box>
                                                <FormControl>
                                                    <RadioGroup
                                                        aria-labelledby="demo-controlled-radio-2"
                                                        name="category"
                                                        value={category}
                                                        onChange={handleCategories}
                                                    >
                                                        <FormControlLabel sx={{ marginRight: { xs: 0 } }} value="internal" control={<Radio />} label="Saved Jobs" />
                                                        <FormControlLabel sx={{ marginRight: { xs: 0 } }} value="external" control={<Radio />} label="Pending Jobs" />
                                                    </RadioGroup>
                                                </FormControl>
                                            </Box>
                                        </Box>
                                    </TabPanel>
                                }
                                <Box className="filter-by-actions-btn">
                                    <Stack direction="row" spacing={2}>
                                        <Button variant="outlined" fullWidth onClick={() => { handlePopoverClose(true) }}>{filterApplied ? "Clear Filter" : "Cancel"}</Button>
                                        <Button variant="contained" fullWidth onClick={handleApplyFilter}>Apply</Button>
                                    </Stack>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Drawer >) : (
                <Popover
                    elevation={3}
                    id="filter-by"
                    className="filter-by-overlay"
                    open={isFilterOpen}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    onClose={() => handlePopoverClose(false)}
                    sx={{
                        top: '15px',
                        left: '-150px'
                    }}
                    disableRestoreFocus
                >
                    <Box
                        sx={{
                            position: "relative",
                            "&::before": {
                                backgroundColor: "white",
                                content: '""',
                                display: "block",
                                position: "absolute",
                                width: 12,
                                height: 12,
                                top: -8,
                                transform: "rotate(45deg)",
                                left: "calc(25% - 7px)",
                                borderTop: '1px solid #D2D5DB',
                                borderLeft: '1px solid #D2D5DB'
                            }
                        }}
                    />
                    <Box sx={{ height: 400, width: { xs: '100%', md: '600px' } }}>
                        <Grid container sx={{ height: '100%' }}>
                            <Grid item xs={4} md={5} className="filter-by-tabs-item">
                                <Tabs
                                    orientation="vertical"
                                    variant="scrollable"
                                    value={value}
                                    onChange={handleChange}
                                    aria-label="Vertical tabs example"
                                    sx={{ height: '100%' }}
                                >
                                    <Tab label="Sort By" disableRipple {...a11yProps(0)} />
                                    <Tab label="Location" disableRipple {...a11yProps(1)} />
                                    <Tab label="Job Suitability" disableRipple {...a11yProps(2)} />
                                    <Tab label="Job Type" disableRipple {...a11yProps(3)} />
                                    {activeTab === 'saved-jobs' && <Tab label="Application Status" disableRipple {...a11yProps(4)} />}
                                </Tabs>
                            </Grid>

                            <Grid item xs={8} md={7} className="filter-by-tabs-content">
                                <TabPanel value={value} index={0} className="tab-panel-wrapper">
                                    <Box>
                                        <Box>
                                            <Typography className="title">Sort By</Typography>
                                        </Box>
                                        <Box>
                                            <FormControl>
                                                <RadioGroup
                                                    aria-labelledby="demo-controlled-radio-1"
                                                    name="salary"
                                                    value={sortValue}
                                                    onChange={handleSortChange}
                                                >
                                                    <FormControlLabel value="1" control={<Radio onDoubleClick={handleDoubleClick} />} label="Compensation (High to Low)" />
                                                    <FormControlLabel value="0" control={<Radio onDoubleClick={handleDoubleClick} />} label="Compensation (Low to High)" />
                                                    <FormControlLabel value="11" control={<Radio onDoubleClick={handleDoubleClick} />} label="Posted (Latest First)" />
                                                    <FormControlLabel value="00" control={<Radio onDoubleClick={handleDoubleClick} />} label="Posted (Oldest First)" />
                                                </RadioGroup>
                                            </FormControl>
                                            <Divider sx={{ borderStyle: 'dashed', borderWidth: '1px', marginTop: '0.5rem', marginBottom: '0.5rem' }} />
                                        </Box>
                                    </Box>
                                </TabPanel>
                                <TabPanel value={value} index={1} className="tab-panel-wrapper">
                                    <Box>
                                        <Box>
                                            <Typography className="title">Location</Typography>
                                            <FormControl>
                                                <FormGroup>
                                                    {preferedLocation?.map((data, index) => (
                                                        <FormControlLabel
                                                            key={index}
                                                            control={
                                                                <Checkbox
                                                                    checked={filterCriteria?.location?.some((fl) => (clusteredData?.find(ct => ct?.value === fl)?.name === data || fl === data))}
                                                                    onChange={(e) => handleChangeFilterCriteria(e, clusteredData?.find(ct => ct?.name === data)?.value || data)}
                                                                    name="location"
                                                                />
                                                            }
                                                            label={data}
                                                        />
                                                    ))}
                                                </FormGroup>
                                            </FormControl>
                                        </Box>
                                    </Box>
                                </TabPanel>
                                <TabPanel value={value} index={2} className="tab-panel-wrapper">
                                    <Box>
                                        <Typography className="title">Job Suitability</Typography>
                                        <FormControl>
                                            <FormGroup>

                                                {["Highly Suitable", "Moderately Suitable", "Not Suitable"]?.map(data => (
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                name="work_format"
                                                                checked={filterCriteria?.work_format?.includes(data)}
                                                                onChange={(e) => handleChangeFilterCriteria(e, data)}
                                                                value={data} />
                                                        }
                                                        label={data} />
                                                ))}
                                            </FormGroup>
                                        </FormControl>
                                    </Box>
                                </TabPanel>
                                <TabPanel value={value} index={3} className="tab-panel-wrapper">
                                    <Box>
                                        <FormControl>
                                            <FormGroup>
                                                <Typography className="title">Job Type</Typography>
                                                {["Full time", "Part time", "Temporary", "Internship"]?.map((jobType, index) => (
                                                    <FormControlLabel
                                                        key={index}
                                                        control={
                                                            <Checkbox
                                                                checked={filterCriteria.job_type.includes(jobType)}
                                                                onChange={(e) => handleChangeFilterCriteria(e, jobType)}
                                                                name="job_type"
                                                            />
                                                        }
                                                        label={jobType}
                                                    />
                                                ))}
                                            </FormGroup>
                                        </FormControl>
                                    </Box>
                                </TabPanel>
                                {activeTab === 'saved-jobs' &&
                                    <TabPanel value={value} index={4} className="tab-panel-wrapper">
                                        <Box>
                                            <Box>
                                                <Typography className="title">Application Status</Typography>
                                            </Box>
                                            <Box>
                                                <FormControl>
                                                    <RadioGroup
                                                        aria-labelledby="demo-controlled-radio-2"
                                                        name="category"
                                                        value={category}
                                                        onChange={handleCategories}
                                                    >
                                                        <FormControlLabel sx={{ marginRight: { xs: 0 } }} value="internal" control={<Radio />} label="Saved Jobs" />
                                                        <FormControlLabel sx={{ marginRight: { xs: 0 } }} value="external" control={<Radio />} label="Pending Jobs" />
                                                    </RadioGroup>
                                                </FormControl>
                                            </Box>
                                        </Box>
                                    </TabPanel>
                                }
                                <Box className="filter-by-actions-btn">
                                    <Stack direction="row" spacing={2}>
                                        <Button variant="outlined" fullWidth onClick={() => { handlePopoverClose(true) }}>{filterApplied ? "Clear Filter" : "Cancel"}</Button>
                                        <Button variant="contained" fullWidth onClick={handleApplyFilter}>Apply</Button>
                                    </Stack>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Popover>)
            }

        </>
    )
}

export default FilterSearch
